'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import { SegmentGroup as SegmentGroupPrimitive } from '@ark-ui/react/segment-group';
import { cva } from 'class-variance-authority';
import { cn } from 'cn';
export { useSegmentGroup } from '@ark-ui/react/segment-group';
export const segmentGroupVariants = cva(['group/segment-group isolate inline-flex w-fit items-center justify-center rounded-4xl bg-muted p-0.75 text-muted-foreground', 'data-vertical:h-fit data-vertical:flex-col data-vertical:items-stretch data-vertical:rounded-2xl'], {
  variants: {
    size: {
      sm: 'h-8',
      default: 'h-9',
      lg: 'h-10'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});
type SegmentGroupProps = Omit<React.ComponentProps<typeof SegmentGroupPrimitive.Root>, 'asChild'> & VariantProps<typeof segmentGroupVariants>;
export function SegmentGroup(props) {
  const $ = _c(16);
  let children;
  let className;
  let rest;
  let t0;
  let t1;
  if ($[0] !== props) {
    ({
      className,
      size: t0,
      orientation: t1,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
    $[4] = t0;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
    t0 = $[4];
    t1 = $[5];
  }
  const size = t0 === undefined ? "default" : t0;
  const orientation = t1 === undefined ? "horizontal" : t1;
  let t2;
  if ($[6] !== className || $[7] !== size) {
    t2 = cn(segmentGroupVariants({
      size
    }), className);
    $[6] = className;
    $[7] = size;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SegmentGroupIndicator />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children || $[11] !== orientation || $[12] !== rest || $[13] !== size || $[14] !== t2) {
    t4 = <SegmentGroupPrimitive.Root data-slot="segment-group" data-size={size} orientation={orientation} className={t2} {...rest}>{t3}{children}</SegmentGroupPrimitive.Root>;
    $[10] = children;
    $[11] = orientation;
    $[12] = rest;
    $[13] = size;
    $[14] = t2;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
type SegmentGroupRootProviderProps = Omit<React.ComponentProps<typeof SegmentGroupPrimitive.RootProvider>, 'asChild'> & VariantProps<typeof segmentGroupVariants>;
export function SegmentGroupRootProvider(props) {
  const $ = _c(14);
  let children;
  let className;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      className,
      size: t0,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
    $[4] = t0;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
    t0 = $[4];
  }
  const size = t0 === undefined ? "default" : t0;
  let t1;
  if ($[5] !== className || $[6] !== size) {
    t1 = cn(segmentGroupVariants({
      size
    }), className);
    $[5] = className;
    $[6] = size;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SegmentGroupIndicator />;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== rest || $[11] !== size || $[12] !== t1) {
    t3 = <SegmentGroupPrimitive.RootProvider data-slot="segment-group" data-size={size} className={t1} {...rest}>{t2}{children}</SegmentGroupPrimitive.RootProvider>;
    $[9] = children;
    $[10] = rest;
    $[11] = size;
    $[12] = t1;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function SegmentGroupIndicator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SegmentGroupPrimitive.Indicator data-slot="segment-group-indicator" className={cn("pointer-events-none absolute top-(--top) left-(--left) z-0 h-(--height) w-(--width) rounded-xl border border-transparent bg-background [--transition-timing-function:ease-in-out]", "motion-reduce:transition-none!", "dark:border-input dark:bg-input/30")} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
type SegmentGroupItemProps = Omit<React.ComponentProps<typeof SegmentGroupPrimitive.Item>, 'asChild'>;
export function SegmentGroupItem(props) {
  const $ = _c(13);
  let children;
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] !== className) {
    t0 = cn("relative z-10 inline-flex h-full flex-1 cursor-pointer items-center justify-center rounded-xl border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all outline-none select-none", "hover:text-foreground", "data-focus-visible:border-ring data-focus-visible:ring-[3px] data-focus-visible:ring-ring/50 data-focus-visible:outline-1 data-focus-visible:outline-ring", "data-invalid:border-destructive data-invalid:ring-destructive/20", "data-checked:text-foreground", "dark:text-muted-foreground", "dark:hover:text-foreground", "dark:data-invalid:ring-destructive/40", "dark:data-checked:text-foreground", "data-disabled:pointer-events-none data-disabled:opacity-50", "data-vertical:h-8.5 data-vertical:w-full data-vertical:justify-start data-vertical:px-2.5 data-vertical:py-1.5", "group-data-[size=lg]/segment-group:data-vertical:h-9.5 group-data-[size=lg]/segment-group:data-vertical:py-2", "group-data-[size=sm]/segment-group:data-vertical:h-7.5 group-data-[size=sm]/segment-group:data-vertical:py-1", "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className);
    $[4] = className;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== children) {
    t1 = <SegmentGroupPrimitive.ItemText data-slot="segment-group-item-text" className="inline-flex items-center gap-1.5">{children}</SegmentGroupPrimitive.ItemText>;
    $[6] = children;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SegmentGroupPrimitive.ItemHiddenInput />;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== rest || $[10] !== t0 || $[11] !== t1) {
    t3 = <SegmentGroupPrimitive.Item data-slot="segment-group-item" className={t0} {...rest}>{t1}{t2}</SegmentGroupPrimitive.Item>;
    $[9] = rest;
    $[10] = t0;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}